/**
 * Was das Buch an Bildern braucht, und wie es dorthin kommt.
 *
 * Fotos und Kartenausschnitte liegen beide nicht als fertige Adresse vor: die
 * Fotos als Blob im lokalen Speicher oder auf dem Server, die Karten überhaupt
 * erst, nachdem sie gerendert wurden. Beides wird hier zu Objekt-URLs gemacht,
 * die die Seiten dann nur noch in ein img-Element setzen.
 *
 * Warum zentral und nicht je Bild wie im Tagebuch: der Druck hängt daran, dass
 * alle Bilder fertig sind, bevor der Druckdialog aufgeht. Ein Bild, das erst
 * während des Druckens geladen wird, fehlt auf dem Papier - ohne Fehlermeldung,
 * ohne dass es jemand merkt, bis er die Seite in der Hand hält. Deshalb lädt
 * eine Stelle alles, zählt mit, und der Vorbereitungsschirm sagt, wann es so
 * weit ist.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { useRoadbook } from '../../data/roadbookContext'
import type { DiaryEntry, PhotoMeta, Stage } from '../../lib/types'

export interface PhotoAssets {
  /** Objekt-URL je Foto-ID. Was fehlt, liess sich nicht laden. */
  urls: Record<string, string>
  /** Wie viele Fotos bearbeitet sind, gelungen oder nicht. */
  loaded: number
  total: number
  /** Alle Fotos sind versucht worden. */
  ready: boolean
}

/**
 * Lädt alle Fotos des Buchs, eines nach dem anderen.
 *
 * Nacheinander und nicht alle gleichzeitig: bei über hundert Bildern wären das
 * hundert offene Anfragen an die Datenbank und, wo lokal nichts liegt, an den
 * Server. Der Fortschritt wäre dann auch keiner mehr - er spränge von 0 auf
 * 100.
 */
export function usePrintPhotos(photos: readonly PhotoMeta[]): PhotoAssets {
  const { loadPhoto } = useRoadbook()

  // Die Liste selbst darf nicht in die Abhängigkeiten: sie ist bei jedem
  // Rendern ein neues Array, und der Effekt liefe endlos. Verglichen wird
  // stattdessen ihr Inhalt, gelesen wird über die Referenz.
  const schluessel = photos.map((foto) => foto.id).join(',')
  const fotosRef = useRef(photos)
  fotosRef.current = photos

  const [urls, setUrls] = useState<Record<string, string>>({})
  const [loaded, setLoaded] = useState(0)
  const [total, setTotal] = useState(photos.length)

  useEffect(() => {
    const liste = [...fotosRef.current]
    let abgebrochen = false
    const eigene: string[] = []

    setUrls({})
    setLoaded(0)
    setTotal(liste.length)

    void (async () => {
      for (const foto of liste) {
        const blob = await loadPhoto(foto.id, foto.stageId).catch(() => null)
        if (abgebrochen) return
        if (blob) {
          const url = URL.createObjectURL(blob)
          eigene.push(url)
          setUrls((vorher) => ({ ...vorher, [foto.id]: url }))
        }
        setLoaded((n) => n + 1)
      }
    })()

    return () => {
      abgebrochen = true
      // Ohne das hält jeder Reiterwechsel die Bilddaten des vorigen Besuchs
      // fest, und das Buch hat sie hundertfach.
      for (const url of eigene) URL.revokeObjectURL(url)
    }
  }, [schluessel, loadPhoto])

  return { urls, loaded, total, ready: total === 0 || loaded >= total }
}

/** Wie weit der Kartenexport ist. */
export type MapPhase = 'leer' | 'laeuft' | 'fertig' | 'fehler'

export interface MapAssets {
  /** Objekt-URL je Etappen-ID und fuer UEBERSICHT_KEY. */
  urls: Record<string, string>
  phase: MapPhase
  done: number
  total: number
  /** Ausschnitte, die nicht zustande kamen. Die Seiten nehmen dort die Skizze. */
  missing: number
  start: () => void
}

/**
 * Rendert die Kartenausschnitte auf Knopfdruck.
 *
 * Ausdrücklich auf Knopfdruck und nicht beim Öffnen des Reiters: der Export
 * baut siebzehn Karten auf, holt dafür Kacheln aus einem grossen Archiv und
 * belegt dabei den einzigen WebGL-Kontext, den die App sonst fuer die
 * Kartenansicht braucht. Wer nur nachsehen will, wie das Buch aussieht, soll das
 * nicht bezahlen müssen - und wer druckt, sieht im Vorbereitungsschirm, was zu
 * tun ist.
 *
 * MapLibre wird erst hier nachgeladen. Ein statischer Import zöge die grösste
 * Abhängigkeit der App in den Start jeder Sitzung, nur weil es einen
 * Druckreiter gibt.
 */
export function usePrintMaps(
  stages: readonly Stage[],
  diary: Record<string, DiaryEntry>,
): MapAssets {
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [phase, setPhase] = useState<MapPhase>('leer')
  const [done, setDone] = useState(0)
  const [total, setTotal] = useState(0)
  const [missing, setMissing] = useState(0)

  const eingabeRef = useRef({ stages, diary })
  eingabeRef.current = { stages, diary }

  // Die fertigen Bilder gehören freigegeben, wenn der Reiter verlassen wird.
  // Sie liegen in einer Referenz und nicht nur im Zustand: die Aufräumfunktion
  // laeuft nach dem letzten Rendern und sähe sonst den Stand von damals.
  const offeneUrls = useRef<string[]>([])
  useEffect(
    () => () => {
      for (const url of offeneUrls.current) URL.revokeObjectURL(url)
      offeneUrls.current = []
    },
    [],
  )

  const laeuftRef = useRef(false)

  const start = useCallback(() => {
    if (laeuftRef.current) return
    laeuftRef.current = true

    setPhase('laeuft')
    setDone(0)
    setMissing(0)
    setTotal(eingabeRef.current.stages.length + 1)

    void (async () => {
      try {
        const { renderPrintMaps } = await import('./mapShots')
        const ergebnis = await renderPrintMaps(eingabeRef.current, (fertig, gesamt) => {
          setDone(fertig)
          setTotal(gesamt)
        })
        offeneUrls.current.push(...Object.values(ergebnis.urls))
        setUrls(ergebnis.urls)
        setMissing(ergebnis.fehlend.length)
        // Kein einziger Ausschnitt ist eine Fehlanzeige und keine Teilmenge:
        // dann fehlt das Archiv oder WebGL, und der Schirm soll es sagen.
        setPhase(Object.keys(ergebnis.urls).length === 0 ? 'fehler' : 'fertig')
      } catch (err) {
        console.error('Der Kartenexport für den Druck ist fehlgeschlagen', err)
        setPhase('fehler')
      } finally {
        laeuftRef.current = false
      }
    })()
  }, [])

  return { urls, phase, done, total, missing, start }
}
